/**
 * EnvironmentVariablesModal Component
 *
 * Modal for managing application environment variables.
 * Accessible from the applications table via a dedicated button.
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { EnvironmentVariablesBuilder } from './EnvironmentVariablesBuilder';
import { useApplication } from '../hooks/useApplication';
import { useUpdateApplication } from '../hooks/useUpdateApplication';
import type { ApplicationSanitized, EnvironmentVariable } from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface EnvironmentVariablesModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  application: ApplicationSanitized;
}

/**
 * EnvironmentVariablesModal component
 */
export const EnvironmentVariablesModal: React.FC<EnvironmentVariablesModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  application,
}) => {
  const [environmentVariables, setEnvironmentVariables] = useState<EnvironmentVariable[]>(
    application.environmentVariables || []
  );
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Fetch latest application data
  const { data: latestApp, isLoading: isLoadingApp } = useApplication(
    tenantId,
    application.applicationId
  );

  // Update local state when latest app data is fetched
  useEffect(() => {
    if (latestApp) {
      setEnvironmentVariables(latestApp.environmentVariables || []);
      setHasChanges(false);
    }
  }, [latestApp]);

  // Reset state when application changes
  useEffect(() => {
    setEnvironmentVariables(application.environmentVariables || []);
    setHasChanges(false);
    setSuccessMessage('');
  }, [application]);

  const updateMutation = useUpdateApplication({
    tenantId,
    applicationId: application.applicationId,
    onSuccess: () => {
      setSuccessMessage('Environment variables saved successfully!');
      setHasChanges(false);
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    },
    onError: (error) => {
      console.error('Failed to update environment variables:', error);
    },
  });

  const handleVariablesChange = (newVars: EnvironmentVariable[]) => {
    setEnvironmentVariables(newVars);
    // Check if variables have changed from original
    const originalVars = latestApp?.environmentVariables || application.environmentVariables || [];
    const changed =
      newVars.length !== originalVars.length ||
      newVars.some((v) => !originalVars.find((ov) => ov.key === v.key && ov.value === v.value)) ||
      originalVars.some((ov) => !newVars.find((v) => v.key === ov.key && v.value === ov.value));
    setHasChanges(changed);
  };

  const handleSave = () => {
    updateMutation.mutate({
      environmentVariables,
    });
  };

  const handleClose = () => {
    setEnvironmentVariables(latestApp?.environmentVariables || application.environmentVariables || []);
    setHasChanges(false);
    setSuccessMessage('');
    onClose();
  };

  const isLoading = updateMutation.isPending;
  const error = updateMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        Environment Variables - {application.applicationName}
      </Modal.Header>

      <Modal.Body>
        {/* Loading State */}
        {isLoadingApp && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {/* Content */}
        {!isLoadingApp && (
          <div className="space-y-4">
            {/* Error Alert */}
            {error && (
              <Alert variant="error">
                {getErrorMessage(error)}
              </Alert>
            )}

            {/* Success Alert */}
            {successMessage && (
              <Alert variant="success">
                {successMessage}
              </Alert>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                Environment variables are available to your application at runtime.
                Keys must be uppercase (e.g., DATABASE_URL, API_KEY).
              </p>
            </div>

            {/* Environment Variables Builder */}
            <EnvironmentVariablesBuilder
              variables={environmentVariables}
              onChange={handleVariablesChange}
              disabled={isLoading}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          {hasChanges ? 'Cancel' : 'Close'}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading || !hasChanges}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
